import GameService from "./game-service";
import FootballStringifier from "./stringifiers/football-stringifier";
import { eventRepository, statsRepository } from "../storage/storage";
import BoxScore from "../sports/football/stats/box-score";
import ContextService from "./context-service";
import FootballTeamStatsCollection from "../sports/football/stats/football-team-stats-collection";
import DriveService from "./drive-service";
import TeamService from "./team-service";
import FootballDriveStatsCollection from "../sports/football/stats/football-drive-stats-collection";
import BaseStatsService from "./base-stats-service";
import ValidationError from "./exceptions/validation-error";

class FootballStatsService extends BaseStatsService {
    async getBoxScore(gameId) {
        const [gameStats, gameContext, game] = await Promise.all([
            this.getByGameId(gameId),
            ContextService.getGameContext(gameId),
            GameService.getGame(gameId)
        ]);

        const event = await eventRepository.getById(game.eventId);
        const { teams } = event;
        const homeTeam = await TeamService.getTeam(teams.homeTeam.id);
        const awayTeam = await TeamService.getTeam(teams.awayTeam.id);

        const teamsStats = [
            new FootballTeamStatsCollection(homeTeam, gameStats),
            new FootballTeamStatsCollection(awayTeam, gameStats)
        ];

        const driveStats = await this.getDriveStatsCollection(
            game.id,
            gameContext,
            gameStats
        );

        const boxScore = new BoxScore(
            gameContext,
            game,
            teamsStats,
            driveStats,
            homeTeam,
            awayTeam,
            event
        );
        const { ruleSetId } = gameContext;
        boxScore.calculate(ruleSetId);
        return boxScore;
    }

    async getGameStatsFormatted(gameId) {
        const [gameStats, game, drives, gameContext] = await Promise.all([
            this.getByGameId(gameId),
            GameService.getGame(gameId),
            DriveService.getByGameId(gameId, true),
            ContextService.getGameContext(gameId)
        ]);

        const event = await eventRepository.getById(game.eventId);
        const { teams } = event;
        const homeTeam = await TeamService.getTeam(teams.homeTeam.id);
        const awayTeam = await TeamService.getTeam(teams.awayTeam.id);

        const teamsStats = [
            new FootballTeamStatsCollection(homeTeam, gameStats),
            new FootballTeamStatsCollection(awayTeam, gameStats)
        ];

        return {
            teams: teamsStats,
            plays: game.plays.map((play) => {
                const { title, messages } = FootballStringifier.summarize(
                    play,
                    gameContext
                );
                messages.unshift(title);
                return {
                    message: messages.reduce((acc, curr) => {
                        return acc + " " + curr;
                    }, "")
                };
            }),
            drives: drives
        };
    }

    async getDriveStatsCollection(gameId, gameContext, gameStats) {
        const drives = await DriveService.getByGameId(gameId, true);
        DriveService.computeDriveTotalTimes(drives, gameContext);

        // Include incomplete drives that already consumed game-clock time
        // (e.g. the final in-progress drive of a game ended at 0:00). Their
        // TOP must still be counted; per-drive scoring logic returns 0/empty
        // for drives with no scoring plays so this is safe downstream.
        const stats = drives
            .filter((d) => d.completed || d.totalTime)
            .map((drive) => {
                const stats = new FootballDriveStatsCollection(
                    drive,
                    gameContext,
                    gameStats
                );
                return stats.toJSON();
            });

        return stats;
    }

    async getByPlayId(playId) {
        return statsRepository.where("playId").equals(playId).toArray();
    }

    async getByDriveId(gameId, driveId) {
        return statsRepository.where({ gameId, driveId }).toArray();
    }

    async deleteByPlayId(playId) {
        return statsRepository.where("playId").equals(playId).delete();
    }

    async savePlayStatLogs(playId, statLogs) {
        if (statLogs.some((p) => p.playId !== playId)) {
            throw new ValidationError("Invalid Play Id");
        }

        await statsRepository.bulkPut(statLogs);

        return statsRepository.where({ playId }).toArray();
    }
}

export default new FootballStatsService();
